import moment from 'moment'
import axios from 'axios'

import AgoraManager from '../agora_manager/agora_manager_get_started.js'
import FirebaseService from './FirebaseService.js'
import ApiService from './ApiService.js'

/**
 * Serviço para gerenciamento de uma sala de telemedicina usando Agora.
 */
class AgoraService {
  /**
   * Construtor do serviço AgoraService.
   * @param {Function} callbackEnterProfessional - Função de callback executada quando o profissional entra.
   * @param {Function} callbackTransmissionQuality - Função de callback para monitorar a qualidade da transmissão.
   * @param {string|null} [cpf=null] - CPF do paciente, opcional.
   * @param {boolean} [saveMovementLog=false] - Indica se deve salvar o log de movimentos durante a teleconsulta.
   * @param {boolean} [updateLevelFirebase=false] - Indica se o nível do Firebase deve ser atualizado durante a teleconsulta.
   */
  constructor (
    callbackEnterProfessional, 
    callbackTransmissionQuality,
    cpf = null,
    pacNome = null,
    protocol = null,
    saveMovementLog = false,
    updateLevelFirebase = false
  ) {
    console.log('CONSTRUCT AgoraService')
    this.agoraAppID = '61cf3781778b4a15a3460bd5ab7814bb'
    this.AgoraManager = null
    this.remotePlayerContainer = null
    this.localPlayerContainer = null

    this.videoOn = true
    this.audioOn = true
    this.blurOn = true

    this.channelParameters = {
      localAudioTrack: null,
      localVideoTrack: null,
      remoteAudioTrack: null,
      remoteVideoTrack: null,
      remoteUid: null
    }
    this.agoraToken = null
    this.patientDropCall = false
    this.joinParameters = null

    this.cpf = cpf
    this.pacNome = pacNome
    this.protocol = protocol
    this.saveMovementLog = saveMovementLog
    this.updateLevelFirebase = updateLevelFirebase
    this.lastSaveLogTime = moment()
    this.limitTimeSaveTeleconsultMovimentLog = 0

    this.FirebaseService = new FirebaseService()
    this.ApiService = new ApiService()

    this.callbackEnterProfessional = callbackEnterProfessional
    this.callbackTransmissionQuality = callbackTransmissionQuality

    this.currentCamera = 'front'

    this.initialize()
  }

  setProtocol (protocol) {
    this.protocol = protocol
  }

  /**
   * Inicializa os containers de vídeo e o gerenciador Agora.
   */
  async initialize () {
    this.remotePlayerContainer = document.createElement('div')
    this.localPlayerContainer = document.createElement('div')
    this.AgoraManager = await AgoraManager(
      this.handleEvents.bind(this), 
      this.handleTransmissionQuality.bind(this)
    )
  }

  /**
   * Lida com os eventos de Qualidade de Transmissção.
   * @param {string} eventName - Nome do evento.
   * @param  {...any} args - Argumentos relacionados ao evento.
   */
  handleTransmissionQuality = (quality) => {
    let level = this._calculateConnectionQuality(quality);
    const agoraRtt = quality.sendRttMs
    let navigatorRtt = 0
    let navigatorType = ''
 
    if (navigator.connection) {
      navigatorRtt = navigator.connection.rtt;
      navigatorType = navigator.connection.effectiveType;
      if (navigatorRtt === 0 && agoraRtt > 0) {
        level = 1;
      } else if (navigatorRtt === 0) {
        level = 0;
      } else if (navigatorRtt >= 600) {
        level = 1;
      }
    }
    quality.level = level;

    // Verifica se deve salvar o log e se CPF está disponível
    if (this.saveMovementLog && this.cpf) {
      const currentMoment = moment();
      const timeDiff = currentMoment.diff(this.lastSaveLogTime); // Calcula a diferença em milissegundos
      const minutes = Math.floor(timeDiff / 60000); // Converte para minutos
      const seconds = Math.floor((timeDiff % 60000) / 1000); // Obtém o restante em segundos
      // Salva o log se passaram pelo menos 5 minuto
      if (minutes >= this.limitTimeSaveTeleconsultMovimentLog) {
        this.limitTimeSaveTeleconsultMovimentLog = 5
        const payloadLog = {
          type: 'INSERT',
          origem: 'Portal Paciente SUS',
          protocol: this.protocol || null,
          pacNome: this.pacNome,
          cpf: this.cpf || 'Não informado',
          payload: JSON.stringify(quality),
          createdAt: moment().format('YYYY-MM-DD HH:mm:ss'),
          subtype: `Nível de Transmissão: ${quality.level}. RTT: ${agoraRtt}ms` // Corrigido para agoraRtt
        };
        // console.log(payloadLog);
        this.ApiService.saveTeleconsultMovimentLog(payloadLog);
        this.lastSaveLogTime = moment(); // Atualiza o timestamp da última chamada
      }
    }
    // Atualiza a qualidade da transmissão no Firebase se necessário
    if (this.updateLevelFirebase && this.cpf) {
      const saveMovementLog = false; // Definição de flag para salvar log
      const upLevel = `${quality.level} (Agora: ${agoraRtt}ms) (tipo: ${navigatorType} ${navigatorRtt}ms)`
      this.FirebaseService.updateFirebaseRoom(this.cpf, 'pacienteNivelTransmissao', upLevel, saveMovementLog);
    }
    this.callbackTransmissionQuality(quality); // Chama o callback
  };
  
  /**
   * Lida com os eventos do Agora.
   * @param {string} eventName - Nome do evento.
   * @param  {...any} args - Argumentos relacionados ao evento.
   */
  handleEvents = (eventName, ...args) => {
    switch (eventName) {
      case 'user-published':
        this._handleUserPublished(args)
        break
      case 'user-unpublished':
        const arg = args[0]
        if (arg._video_muted_ === false && arg._audio_muted_ === false) {
          this.patientDropCall = true
          const value = null
          this.FirebaseService.updateFirebaseRoom(this.joinParameters.cpf, 'funStreamId', value)
          setTimeout(() => {
            this.FirebaseService.updateFirebaseRoom(this.joinParameters.cpf, 'funNome', value)
          }, 50)
          this.callbackEnterProfessional(false)
        }
        break
    }
  }

  /**
   * Cria uma nova sala de vídeo.
   * @param {string} cpf - CPF do usuário.
   * @param {Function} callback - Função de callback para retorno do status.
   */
  async createRoom (cpf, callback) {
    console.warn('[AgoraService] Criar sala')
    this.joinParameters = null

    // Obter token para criar a sala
    this.agoraToken = await this._getAgoraToken(cpf)
    if (!this.agoraToken) {
      await callback({ status: false, msg: 'Não foi possível criar a sala.' })
      return
    }

    this.joinParameters = {
      appId: this.agoraAppID,
      channelName: this.agoraToken.canal,
      token: this.agoraToken.token,
      cpf: this.agoraToken.cpf
    }

    const localStreamDiv = document.getElementById('local_stream')
    await this.AgoraManager.join(
      localStreamDiv, this.localPlayerContainer, 
      this.channelParameters, this.joinParameters
    )

    await callback({ status: true, agoraToken: this.agoraToken })
  }

  /**
   * Finaliza a transmissão de vídeo.
   * @param {Function} callback - Função de callback para retorno do status.
   */
  async closeVideo (callback) {
    const obj = { status: true }

    if (!this.AgoraManager) {
      callback(obj)
      return
    }

    await this.AgoraManager.leave(this.channelParameters)
    this._removeView('local_video')

    if (this.remotePlayerContainer?.id) {
      this._removeView(this.remotePlayerContainer.id)
    }

    callback(obj)
  }

  /**
   * Ativa ou desativa o vídeo.
   * @param {Function} callback - Função de callback para retorno do status.
   */
  videoOnOff (callback) {
    this.videoOn = !this.videoOn
    this.channelParameters.localVideoTrack.setMuted(!this.videoOn)
    callback({ status: this.videoOn })
  }

  /**
   * Ativa ou desativa o áudio.
   * @param {Function} callback - Função de callback para retorno do status.
   */
  audioOnOff (callback) {
    this.audioOn = !this.audioOn
    this.channelParameters.localAudioTrack.setMuted(!this.audioOn)
    callback({ status: this.audioOn })
  }

  /**
   * Ativa ou desativa o efeito de desfoque de fundo.
   * @param {Function} callback - Função de callback para retorno do status.
   */
  async blurOnOff (callback) {
    this.blurOn = !this.blurOn

    if (this.blurOn) {
      await this.AgoraManager.setBackgroundColor(this.channelParameters)
    } else {
      await this.AgoraManager.disableBackground(this.channelParameters)
    }

    callback({ status: this.blurOn })
  }

  /**
   * Altera o elemento HTML onde o stream de vídeo local será exibido.
   * @async
   * @param {HTMLElement} div - O elemento HTML onde o vídeo local será reproduzido.
   * @param {function} [callback] - Função de callback opcional a ser chamada 
   * após a reprodução do vídeo.
   */
  async changeLocalStreamDiv (div, callback) {
    if (!div) {
      return
    }
    this.channelParameters.localVideoTrack.play(div)
    if (!callback) {
      callback(true)
    }
  }

  /**
   * Alterna entre a câmera frontal e traseira.
   * Este método obtém as câmeras disponíveis, destrói o track de vídeo atual
   * e cria um novo track com a câmera oposta (frontal ou traseira).
   * @async
   * @param {Function} callback - Função de callback para indicar o status da troca. 
   * Recebe um objeto com o status e a câmera atual ou erro.
   */
  async toggleCamera(callback) {
    // Obter dispositivos de vídeo (câmeras)
    const devices = await this.AgoraManager.getAgoraRTC().getDevices();
    const videoDevices = devices.filter(device => device.kind === 'videoinput');
    console.warn('devices', videoDevices)

    if (videoDevices.length > 1) {
      // Determina a próxima câmera a ser usada
      const targetDeviceId = this.currentCamera === 'front'
        ? videoDevices[videoDevices.length - 1].deviceId // Troca para a câmera traseira
        : videoDevices[0].deviceId; // Troca para a câmera frontal
      console.warn('targetDeviceId', targetDeviceId)

      try {
        // Destrói o vídeo atual
        this.channelParameters.localVideoTrack.close();

        // Cria um novo track de vídeo com a nova câmera
        const newLocalVideoTrack = await this.AgoraManager.getAgoraRTC().createCameraVideoTrack({ 
          cameraId: targetDeviceId 
        });

        // Substitui o track de vídeo anterior
        await this._switchCamera(newLocalVideoTrack);

        // Alterna o estado da câmera atual
        this.currentCamera = this.currentCamera === 'front' ? 'back' : 'front';
        callback({ status: true, currentCamera: this.currentCamera });
      } catch (error) {
        // retorna para o video original
        const newLocalVideoTrack = await this.AgoraManager.getAgoraRTC().createCameraVideoTrack({ 
          cameraId: videoDevices[0].deviceId 
        });
        await this._switchCamera(newLocalVideoTrack);
        console.error('Erro ao alternar câmera:', error);
        callback({ status: false, error });
      }
    } else {
      console.log('Nenhuma outra câmera disponível.');
      callback({ status: false, msg: 'Nenhuma outra câmera disponível.' });
    }
  }

  /**
   * Substitui o track de vídeo atual por um novo track.
   * Este método despublica o track de vídeo anterior, para o vídeo e 
   * libera o track da memória, depois publica o novo track e vincula 
   * ao container HTML de vídeo.
   * @async
   * @private
   * @param {Object} newLocalVideoTrack - O novo track de vídeo gerado pela nova câmera.
   */
  async _switchCamera(newLocalVideoTrack) {
    try {
      // Verificar se há um track de vídeo publicado
      if (this.channelParameters.localVideoTrack) {
        // Despublicar o track de vídeo anterior
        await this.AgoraManager.getAgoraEngine().unpublish(this.channelParameters.localVideoTrack);
        // Parar o track de vídeo anterior
        this.channelParameters.localVideoTrack.stop();
        // Liberar o track de vídeo anterior da memória
        this.channelParameters.localVideoTrack.close();
      }

      // Publicar o novo track de vídeo
      await this.AgoraManager.getAgoraEngine().publish([newLocalVideoTrack]);
      const videoContainerId = 'local_video'; // Substitua com o ID do seu container de vídeo
      newLocalVideoTrack.play(videoContainerId); // Vincular o novo track ao elemento HTML

      // Atualizar a referência para o novo track de vídeo
      this.channelParameters.localVideoTrack = newLocalVideoTrack;
      console.log('Câmera alternada com sucesso.');
    } catch (error) {
      console.error('Erro ao alternar a câmera:', error);
    }
  }

  /**
   * Obtém o token do Agora para criar a sala.
   * @private
   * @param {string} cpf - CPF do paciente.
   * @returns {Object|null} Objeto com informações do token ou null em caso de erro.
   */
  async _getAgoraToken (cpf) {
    const channel = `CA-pacCpf_${cpf}`
    const objAgora = { canal: channel, uid: channel }

    try {
      const url = `${process.env.VUE_APP_LOTUS_MINHA_SAUDE}/api/telemedicina/gerar-token`
      const response = await axios.post(url, objAgora)
      const token = response.data.token

      // Gerar código de identificação
      const now = new Date()
      const dateBlock = `${now.getFullYear().toString().slice(-1)}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}`
      const hourBlock = `${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${Math.floor(now.getMilliseconds() / 10).toString().padStart(2, '0')}`
      const thirdBlock = Array(3).fill(null).map(() => 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'.charAt(Math.floor(Math.random() * 36))).join('')

      return { token, canal: objAgora.canal, uid: objAgora.uid, cpf, code: `${dateBlock}-${hourBlock}-${thirdBlock}`, bloqueado: 0 }
    } catch (error) {
      console.error('GET TOKEN. ERROR:', error)
      return null
    }
  }

  /**
   * Remove um elemento de vídeo da DOM.
   * @private
   * @param {string} id - ID do elemento a ser removido.
   */
  _removeView (id) {
    console.warn('Removing ' + id + ' Div')
    const div = document.getElementById(id)
    if (div) {
      div.remove()
    }
  }

  /**
   * Lida com o evento de publicação de vídeo de um usuário.
   * @private
   * @param {Array} args - Argumentos relacionados ao evento de publicação do usuário.
   */
  _handleUserPublished (args) {
    if (args[1] === 'video') {
      const remoteUid = args[0].uid.toString() // streamId de quem está entrando
      this.patientDropCall = false
      this.channelParameters.remoteVideoTrack = args[0].videoTrack
      this.channelParameters.remoteAudioTrack = args[0].audioTrack
      this.channelParameters.remoteUid = remoteUid
      this.remotePlayerContainer.id = remoteUid
      this.remotePlayerContainer.style.width = '100vw'
      this.remotePlayerContainer.style.height = '100vh'
      document.getElementById('expand_stream').appendChild(this.remotePlayerContainer)
      this.channelParameters.remoteVideoTrack.play(this.remotePlayerContainer)
      // Reproduz a trilha de áudio, se disponível
      if (this.channelParameters.remoteAudioTrack) {
        this.channelParameters.remoteAudioTrack.setVolume(100);
        this.channelParameters.remoteAudioTrack.play() // Reproduz o áudio
      }
      this.callbackEnterProfessional(true)
    }
  }

  /**
   * Calcula o nível de qualidade da conexão com base em vários parâmetros.
   * @private
   * @param {Object} params - Os parâmetros da conexão.
   * @returns {number} - O nível de qualidade da conexão (de 1 a 5).
   */
  _calculateConnectionQuality ({downlinkNetworkQuality, uplinkNetworkQuality}) {
    const qualityAverage = Math.ceil(
        (downlinkNetworkQuality + uplinkNetworkQuality) / 2
    )
    switch (qualityAverage) {
      case 1:
        return 5
      case 2:
        return 4
      case 3:
        return 3
      case 4:
        return 2
      case 5:
        return 1
      default:
        return 0
    }
  }
}

export default AgoraService
